/**
 * Drawing coordinate helpers.
 *
 * Converts world-mm positions to sheet-pixel positions used by
 * FloorPlanDrawing, so the cut overlay SVG stays aligned with the
 * rendered plan regardless of drawing scale or sheet size.
 */
import { worldToPx, mmToPx } from "./DrawingUtils";
import { SHEETS } from "../../engine/drawingStandards";

/**
 * Returns { offX, offY, minX, minY } — the pixel origin of the plan
 * within the sheet at the given scale and sheetKey.
 */
export function getPlanOrigin(st, scale, sheetKey = "A3") {
  const sheet   = SHEETS[sheetKey] || SHEETS.A3;
  const margin  = mmToPx(15);
  const dimSpace= mmToPx(25);
  const titleH  = mmToPx(30);

  const drawX = margin + dimSpace;
  const drawY = margin + dimSpace;
  const drawW = mmToPx(sheet.w) - margin - dimSpace - margin;
  const drawH = mmToPx(sheet.h) - titleH - margin * 2 - dimSpace;

  const nodes = Object.values(st.nodes || {});
  if (!nodes.length) return { offX: drawX, offY: drawY, minX: 0, minY: 0 };

  const xs     = nodes.map(n => n.x), ys = nodes.map(n => n.y);
  const minX   = Math.min(...xs),    minY = Math.min(...ys);
  const maxX   = Math.max(...xs),    maxY = Math.max(...ys);
  const planWpx = worldToPx(maxX - minX, scale);
  const planHpx = worldToPx(maxY - minY, scale);

  const offX = drawX + (drawW - planWpx) / 2 - worldToPx(minX, scale);
  const offY = drawY + (drawH - planHpx) / 2 - worldToPx(minY, scale);

  return { offX, offY, minX, minY };
}

/**
 * Convert a world-mm point { x, y } to sheet-pixel { x, y }.
 */
export function worldToSheet(pt, st, scale, sheetKey) {
  const { offX, offY } = getPlanOrigin(st, scale, sheetKey);
  return {
    x: offX + worldToPx(pt.x, scale),
    y: offY + worldToPx(pt.y, scale),
  };
}
